import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/dateUtils';
import { cropStyle } from '../utils/photoCrop';
import { TrendingUp, TrendingDown, DollarSign, Car, BarChart2, Award, Plus, Camera, ClipboardList, MoreHorizontal } from 'lucide-react';
import { isNative } from '../utils/native';
import { useDisplayName } from '../hooks/useDisplayName';
import { useOwner } from '../hooks/useOwner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 13, fontWeight: 600, color: p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </div>
      ))}
      {payload[0]?.payload?.vehicles > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{payload[0].payload.vehicles} vehicles counted</div>
      )}
    </div>
  );
};

export const Portfolio = () => {
  const navigate = useNavigate();
  const { firstName } = useDisplayName();
  const { role } = useOwner();
  const canEdit = ['owner', 'admin', 'editor'].includes(role);
  const [vehicles, setVehicles]     = useState([]);
  const [valuations, setValuations] = useState([]);
  const [histories, setHistories]   = useState([]);
  const [expenses, setExpenses]     = useState([]);
  const [insurance, setInsurance]   = useState([]);
  const [roadTax, setRoadTax]       = useState([]);
  const [services, setServices]     = useState([]);
  const [mechanical, setMechanical] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAllAlloc, setShowAllAlloc] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [v, val, hist, exp, ins, rt, svc, mech] = await Promise.all([
      supabase.from('vehicles').select('*, vehicle_photos(url, is_primary, focal_x, focal_y, zoom)').in('status', ['active','inactive','classic','daneira','immobilized','dokimi_test','service','restoration']),
      supabase.from('vehicle_valuations').select('*'),
      supabase.from('valuation_history').select('*').order('valuation_date', { ascending: true }),
      supabase.from('expenses').select('*'),
      supabase.from('insurance_records').select('*'),
      supabase.from('road_tax_records').select('*'),
      supabase.from('service_records').select('*'),
      supabase.from('mechanical_reports').select('*'),
    ]);
    setVehicles(v.data || []);
    setValuations(val.data || []);
    setHistories(hist.data || []);
    setExpenses(exp.data || []);
    setInsurance(ins.data || []);
    setRoadTax(rt.data || []);
    setServices(svc.data || []);
    setMechanical(mech.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  // ── Portfolio calculations ──────────────────────────────────────────────────
  const vehicleData = vehicles.map(v => {
    const val = valuations.find(vl => vl.vehicle_id === v.id);
    const purchasePrice  = val?.purchase_price  ? Number(val.purchase_price)  : null;
    const currentValue   = val?.current_value   ? Number(val.current_value)   : null;

    // Restoration is a capital improvement, not a running cost — it adds to invested capital
    const vExpenses = expenses.filter(e => e.vehicle_id === v.id);
    const restorationSpend = vExpenses
      .filter(e => e.category === 'Restoration')
      .reduce((s, e) => s + Number(e.amount || 0), 0);
    const investedCapital = purchasePrice ? purchasePrice + restorationSpend : null;

    const gainLoss       = investedCapital && currentValue ? currentValue - investedCapital : null;
    const gainLossPct    = gainLoss !== null && investedCapital ? (gainLoss / investedCapital) * 100 : null;

    // Running costs — everything except restoration
    const runningCosts =
      vExpenses.filter(e => e.category !== 'Restoration').reduce((s, e) => s + Number(e.amount || 0), 0) +
      insurance.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.cost || 0), 0) +
      roadTax.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.amount_paid || 0), 0) +
      services.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.cost || 0), 0) +
      mechanical.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.cost || 0), 0);

    const netReturn = gainLoss !== null ? gainLoss - runningCosts : null;
    // THE return % used everywhere (cards, table, chart): net return as a share of everything
    // actually put into the car (purchase + restoration + running costs). Same method as the
    // vehicle page and Car Intelligence, so a car shows one percentage on every page.
    const returnPct = netReturn !== null && (investedCapital + runningCosts) > 0
      ? (netReturn / (investedCapital + runningCosts)) * 100 : null;
    // Profile photo (is_primary), falling back to the first photo for older
    // rows uploaded before is_primary existed. Keep the whole row (not just
    // the url) so cards can crop/zoom it the way it was set in the gallery.
    const profilePhotoRow = v.vehicle_photos?.find(p => p.is_primary) || v.vehicle_photos?.[0] || null;
    const photo = profilePhotoRow?.url || null;

    return { ...v, val, purchasePrice, restorationSpend, investedCapital, currentValue, gainLoss, gainLossPct, runningCosts, netReturn, returnPct, photo, photoRow: profilePhotoRow };
  });

  // Fleet value counts every valued vehicle, but gain/return must only compare vehicles
  // that have BOTH an invested figure and a current value — otherwise costs from unvalued
  // cars are subtracted against gains those cars never got credited for.
  const comparable = vehicleData.filter(v => v.investedCapital && v.currentValue);

  const totalCurrentValue  = vehicleData.reduce((s, v) => s + (v.currentValue || 0), 0);
  const comparableValue    = comparable.reduce((s, v) => s + v.currentValue, 0);
  const totalPurchasePrice = comparable.reduce((s, v) => s + (v.purchasePrice || 0), 0);
  const totalRestoration   = comparable.reduce((s, v) => s + (v.restorationSpend || 0), 0);
  const totalInvested      = totalPurchasePrice + totalRestoration;
  const totalGainLoss      = comparableValue - totalInvested;
  const totalGainLossPct   = totalInvested > 0 ? (totalGainLoss / totalInvested) * 100 : 0;

  // Full sums across every vehicle that has the figure at all, regardless of whether
  // the OTHER figure is also known — these are what the "Invested" and "Current Value"
  // TOTAL cells display, because that's what a reader actually checks: adding up the
  // column. (comparableValue/totalInvested above stay restricted to vehicles with BOTH
  // figures — that pairing is still needed for Gain/Loss, Return % and Net Return,
  // which can't be computed from one figure alone.)
  const totalInvestedAll    = vehicleData.reduce((s, v) => s + (v.investedCapital || 0), 0);
  const totalRestorationAll = vehicleData.reduce((s, v) => s + (v.investedCapital != null ? v.restorationSpend : 0), 0);

  const totalRunningCosts      = vehicleData.reduce((s, v) => s + v.runningCosts, 0);
  const comparableRunningCosts = comparable.reduce((s, v) => s + v.runningCosts, 0);
  const otherRunningCosts      = totalRunningCosts - comparableRunningCosts;
  const unvaluedCount          = vehicleData.length - comparable.length;
  // Valuations with 0 comparables have no market evidence behind them
  const unbacked      = vehicleData.filter(v => v.val?.valuation_comparables === 0 && v.currentValue);
  const unbackedValue = unbacked.reduce((s, v) => s + v.currentValue, 0);
  const totalNetReturn         = totalGainLoss - comparableRunningCosts;
  const valuedVehicles     = vehicleData.filter(v => v.currentValue);
  const appreciating       = vehicleData.filter(v => v.gainLoss > 0).length;
  const depreciating       = vehicleData.filter(v => v.gainLoss < 0).length;

  // Sort by net return % (after running costs). Cars whose value has no comparable sales
  // behind it are still listed, but flagged with a "No market data" tag on the card.
  const byPerformance = [...vehicleData]
    .filter(v => v.returnPct !== null)
    .sort((a, b) => b.returnPct - a.returnPct);
  const topPerformers   = byPerformance.filter(v => v.returnPct > 0).slice(0, 3);
  const worstPerformers = byPerformance.filter(v => v.returnPct < 0).slice(-3).reverse();
  const totalReturnBase = totalInvested + comparableRunningCosts;
  const totalReturnPct  = totalReturnBase > 0 ? (totalNetReturn / totalReturnBase) * 100 : 0;
  const noMarketData = v => v.val?.valuation_comparables === 0;

  // Portfolio value history from valuation_history.
  // Built per vehicle, then summed, so one car can never be counted twice in a month
  // and a car that wasn't re-valued in a month still counts at its last known value:
  //   1. a vehicle's value for a month = its LATEST entry logged in that month
  //   2. a month with no entry carries forward the vehicle's previous value
  //   3. the current month uses each vehicle's live Estimated Market Value, so the
  //      last point always agrees with the "Total Fleet Value" card above
  // Before the monthly auto-snapshot existed, entries were only logged when someone
  // opened Car Intelligence, which left some months with a few cars missing and
  // others with a car counted several times — both made the monthly totals wrong.
  const monthKey = d => d.slice(0, 7);
  const thisMonth = monthKey(new Date().toISOString());
  const nextMonth = m => { const [y, mo] = m.split('-').map(Number); return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`; };
  const inPortfolio = new Set(vehicles.map(v => v.id));
  const perVehicle = {};   // vehicle id -> { 'YYYY-MM': value }
  [...histories]
    .filter(h => h.valuation_date && inPortfolio.has(h.vehicle_id) && Number(h.value) > 0)
    .sort((a, b) => a.valuation_date.localeCompare(b.valuation_date) || String(a.created_at || '').localeCompare(String(b.created_at || '')))
    .forEach(h => {
      (perVehicle[h.vehicle_id] = perVehicle[h.vehicle_id] || {})[monthKey(h.valuation_date)] = Number(h.value);
    });
  vehicleData.forEach(v => {
    if (v.currentValue) (perVehicle[v.id] = perVehicle[v.id] || {})[thisMonth] = v.currentValue;
  });
  const allMonths = Object.values(perVehicle).flatMap(m => Object.keys(m));
  const firstMonth = allMonths.length ? allMonths.reduce((a, b) => (a < b ? a : b)) : thisMonth;
  const historyData = [];
  const lastKnown = {};
  for (let m = firstMonth; m <= thisMonth; m = nextMonth(m)) {
    let total = 0; let counted = 0;
    Object.entries(perVehicle).forEach(([vid, months]) => {
      if (months[m] !== undefined) lastKnown[vid] = months[m];
      if (lastKnown[vid] !== undefined) { total += lastKnown[vid]; counted += 1; }
    });
    historyData.push({
      month: new Date(m + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      value: total,
      vehicles: counted,
    });
  }

  // Allocation by value
  const allocationData = vehicleData
    .filter(v => v.currentValue)
    .map(v => ({ name: `${v.brand} ${v.model}`, value: v.currentValue }))
    .sort((a, b) => b.value - a.value);

  const COLORS = ['#4f8ef7','#22c55e','#a855f7','#f97316','#eab308','#06b6d4','#ec4899','#6b7280'];

  // Fleet Allocation card: the five biggest vehicles by value get their own colour; everything
  // else is grouped as "Other vehicles" (and listed one by one under "Show all").
  const ALLOC_TOP_N   = 5;
  const OTHER_COLOR   = '#64748b';
  const allocTotal    = allocationData.reduce((s, v) => s + v.value, 0);
  const allocTop      = allocationData.slice(0, ALLOC_TOP_N);
  const allocRest     = allocationData.slice(ALLOC_TOP_N);
  const allocRestValue = allocRest.reduce((s, v) => s + v.value, 0);
  const allocPct      = value => (allocTotal > 0 ? (value / allocTotal) * 100 : 0);
  const allocTopShare = allocPct(allocTop.reduce((s, v) => s + v.value, 0));
  const donutData = [
    ...allocTop.map((v, i) => ({ ...v, color: COLORS[i % COLORS.length] })),
    ...(allocRestValue > 0 ? [{ name: 'Other vehicles', value: allocRestValue, color: OTHER_COLOR }] : []),
  ];
  const compactEuro = n => (n >= 1e6 ? `€${(n / 1e6).toFixed(2)}m` : n >= 1e3 ? `€${Math.round(n / 1e3)}k` : `€${Math.round(n)}`);
  const AllocTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 2 }}>{d.name}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{formatCurrency(d.value)} · {allocPct(d.value).toFixed(1)}%</div>
      </div>
    );
  };
  const AllocRow = ({ name, value, color }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 14, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{allocPct(value).toFixed(1)}%</span>
      </div>
      <div style={{ height: 3, borderRadius: 2, background: 'var(--surface2)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, allocPct(value))}%`, height: '100%', borderRadius: 2, background: color }} />
      </div>
    </div>
  );

  return (
    <div className="page portfolio-page">
      {/* Phone app only: greeting, fleet value and quick actions (the web header is hidden there) */}
      {isNative && (
        <>
          <div className="m-home-header">
            <div className="m-home-greet">Hey {firstName || 'there'}</div>
            <div className="m-home-sub">Here's your portfolio today</div>
            <div className="m-home-valuelabel">Total fleet value</div>
            <div className="m-home-value">{formatCurrency(totalCurrentValue)}</div>
            <div className="m-home-chips">
              <span className="m-chip">{valuedVehicles.length} of {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} valued</span>
              {comparable.length > 0 && (
                <span className={`m-chip ${totalNetReturn >= 0 ? 'is-up' : 'is-down'}`}>
                  {totalReturnPct >= 0 ? '+' : ''}{totalReturnPct.toFixed(1)}% net return
                </span>
              )}
            </div>
          </div>
          <div className="m-quick">
            {[
              canEdit && { icon: Plus, label: 'Add Vehicle', to: '/vehicles/new', primary: true },
              canEdit && { icon: Camera, label: 'Scan Invoice', to: '/expenses?add=1' },
              { icon: ClipboardList, label: 'Forms', to: '/forms' },
              { icon: MoreHorizontal, label: 'View More', to: '/menu', outline: true },
            ].filter(Boolean).map(a => (
              <button key={a.label} className="m-quick-item" onClick={() => navigate(a.to)}>
                <span className={`m-quick-circle ${a.primary ? 'is-primary' : ''} ${a.outline ? 'is-outline' : ''}`}><a.icon size={22} /></span>
                <span className="m-quick-label">{a.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
      <div className="page-header">
        <div>
          <div className="page-title">Portfolio</div>
          <div className="page-subtitle">{vehicles.length} vehicles · {valuedVehicles.length} valued</div>
        </div>
      </div>

      {/* Key metrics */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          {
            label: 'Total Fleet Value',
            value: formatCurrency(totalCurrentValue),
            sub: unbackedValue > 0
              ? `${valuedVehicles.length} of ${vehicles.length} valued · ${formatCurrency(unbackedValue)} unverified`
              : `${valuedVehicles.length} of ${vehicles.length} valued`,
            grad: ['#4f8ef7','#7c3aed'],
            icon: <DollarSign size={20} />,
          },
          {
            label: 'Total Invested',
            value: formatCurrency(totalInvested),
            sub: totalRestoration > 0
              ? `${formatCurrency(totalPurchasePrice)} purchase + ${formatCurrency(totalRestoration)} restoration`
              : `Across ${comparable.length} valued vehicle${comparable.length !== 1 ? 's' : ''}`,
            grad: ['#06b6d4','#4f8ef7'],
            icon: <Car size={20} />,
          },
          {
            label: 'Total Gain / Loss',
            value: formatCurrency(totalGainLoss),
            sub: `Value vs purchase + restoration · ${comparable.length} valued vehicle${comparable.length !== 1 ? 's' : ''}`,
            grad: totalGainLoss >= 0 ? ['#22c55e','#06b6d4'] : ['#ef4444','#f97316'],
            icon: totalGainLoss >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />,
          },
          {
            label: 'Net Return',
            value: formatCurrency(totalNetReturn),
            sub: otherRunningCosts > 0
              ? `${totalReturnPct >= 0 ? '+' : ''}${totalReturnPct.toFixed(1)}% after ${formatCurrency(comparableRunningCosts)} costs · excludes ${formatCurrency(otherRunningCosts)} on ${unvaluedCount} unvalued`
              : `${totalReturnPct >= 0 ? '+' : ''}${totalReturnPct.toFixed(1)}% after ${formatCurrency(comparableRunningCosts)} running costs`,
            grad: totalNetReturn >= 0 ? ['#22c55e','#4f8ef7'] : ['#ef4444','#eab308'],
            icon: <BarChart2 size={20} />,
          },
        ].map((item, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ color: item.grad[0] }}>{item.icon}</div>
              <div className="stat-label">{item.label}</div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, background: `linear-gradient(135deg, ${item.grad[0]}, ${item.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: -0.5 }}>{item.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{item.sub}</div>
          </div>
        ))}
      </div>

      {/* Portfolio value chart */}
      <div style={{ marginBottom: 16 }}>

        {/* Value history chart */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>Portfolio Value History</div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>Total fleet value by month. Logged automatically on the 1st of each month from every car's Estimated Market Value.</div>
          {historyData.length > 1 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={historyData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="portGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f8ef7" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                <Area type="monotone" dataKey="value" name="Portfolio Value" stroke="#4f8ef7" strokeWidth={2.5} fill="url(#portGrad)" dot={false} activeDot={{ r: 6, fill: '#4f8ef7' }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
              <BarChart2 size={32} style={{ color: 'var(--text3)' }} />
              <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>
                Fills in automatically on the 1st of every month from each car's Estimated Market Value<br />
                (needs at least two months of history)
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Fleet allocation */}
      <div className="card" style={{ marginBottom: 16, padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 6 }}>Fleet allocation</div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>A closer look at your collection</div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)', border: '1px solid var(--border)', background: 'var(--surface2)', borderRadius: 8, padding: '8px 14px', whiteSpace: 'nowrap' }}>By current value</div>
        </div>

        {allocationData.length > 0 ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', width: 240, height: 240, maxWidth: '100%', flexShrink: 0, margin: '0 auto' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius="76%" outerRadius="100%" paddingAngle={donutData.length > 1 ? 2 : 0} startAngle={90} endAngle={-270} dataKey="value" stroke="none">
                      {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip content={<AllocTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, pointerEvents: 'none' }}>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>Total fleet value</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--text)', letterSpacing: -0.5 }}>{compactEuro(allocTotal)}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>{allocationData.length} vehicle{allocationData.length !== 1 ? 's' : ''}</div>
                </div>
              </div>

              <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                {allocTop.map((item, i) => <AllocRow key={`${item.name}-${i}`} name={item.name} value={item.value} color={COLORS[i % COLORS.length]} />)}
                {showAllAlloc
                  ? allocRest.map((item, i) => <AllocRow key={`rest-${item.name}-${i}`} name={item.name} value={item.value} color={OTHER_COLOR} />)
                  : allocRestValue > 0 && (
                      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18 }}>
                        <AllocRow name="Other vehicles" value={allocRestValue} color={OTHER_COLOR} />
                      </div>
                    )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderTop: '1px solid var(--border)', marginTop: 28, paddingTop: 18, fontSize: 13 }}>
              <span style={{ color: 'var(--text3)' }}>
                {allocRest.length === 0
                  ? `${allocationData.length} vehicle${allocationData.length !== 1 ? 's' : ''} · 100% of fleet value`
                  : showAllAlloc
                    ? `All ${allocationData.length} vehicles · 100% of fleet value`
                    : `Top ${allocTop.length} vehicles · ${allocTopShare.toFixed(1)}% of fleet value`}
              </span>
              {allocRest.length > 0 && (
                <button type="button" onClick={() => setShowAllAlloc(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text3)', fontSize: 13 }}>
                  {showAllAlloc ? 'Show less' : 'Show all'}
                </button>
              )}
            </div>
          </>
        ) : (
          <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>No values set</div>
        )}
      </div>

      {/* Performance chart */}
      {vehicleData.filter(v => v.netReturn !== null).length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>Vehicle Performance</div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>Net return per vehicle, after running costs</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={vehicleData.filter(v => v.netReturn !== null).map(v => ({ name: `${v.brand} ${v.model}`.slice(0, 15), netReturn: v.netReturn, pct: Number(v.returnPct?.toFixed(1)) }))} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
              <Bar dataKey="netReturn" name="Net return" radius={[4,4,0,0]}>
                {vehicleData.filter(v => v.netReturn !== null).map((v, i) => (
                  <Cell key={i} fill={v.netReturn >= 0 ? '#22c55e' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top & Worst performers */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }} className="dashboard-grid-2">
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Award size={16} style={{ color: '#22c55e' }} />
            <div style={{ fontWeight: 700, fontSize: 17 }}>Top Performers</div>
          </div>
          {topPerformers.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>No valuation data yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topPerformers.map((v, i) => (
                <div key={v.id} onClick={() => navigate(`/vehicles/${v.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 10, cursor: 'pointer' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', background: 'var(--surface2)', flexShrink: 0 }}>
                    {v.photo ? <img src={v.photo} alt="" style={{ width: '100%', height: '100%', ...cropStyle(v.photoRow) }} /> : <Car size={20} style={{ margin: '10px auto', display: 'block', color: 'var(--text3)' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.brand} {v.model}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>{v.registration_number} · {v.year}</div>
                    {noMarketData(v) && <div style={{ fontSize: 10, fontWeight: 600, color: '#f97316', marginTop: 2 }} title="No comparable sales matched this vehicle, so its value isn't backed by market data">⚠ No market data</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#22c55e' }}>+{v.returnPct?.toFixed(1)}%</div>
                    <div style={{ fontSize: 11, color: '#22c55e' }}>+{formatCurrency(v.netReturn)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <TrendingDown size={16} style={{ color: '#ef4444' }} />
            <div style={{ fontWeight: 700, fontSize: 17 }}>Needs Attention</div>
          </div>
          {worstPerformers.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>No valuation data yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {worstPerformers.map((v, i) => (
                <div key={v.id} onClick={() => navigate(`/vehicles/${v.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 10, cursor: 'pointer' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', background: 'var(--surface2)', flexShrink: 0 }}>
                    {v.photo ? <img src={v.photo} alt="" style={{ width: '100%', height: '100%', ...cropStyle(v.photoRow) }} /> : <Car size={20} style={{ margin: '10px auto', display: 'block', color: 'var(--text3)' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.brand} {v.model}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>{v.registration_number} · {v.year}</div>
                    {noMarketData(v) && <div style={{ fontSize: 10, fontWeight: 600, color: '#f97316', marginTop: 2 }} title="No comparable sales matched this vehicle, so its value isn't backed by market data">⚠ No market data</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444' }}>{v.returnPct?.toFixed(1)}%</div>
                    <div style={{ fontSize: 11, color: '#ef4444' }}>{formatCurrency(v.netReturn)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full vehicle table */}
      <div className="card">
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>All Vehicles</div>
        <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>Full portfolio breakdown</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            <span style={{ color: '#22c55e', fontWeight: 700 }}>{appreciating}</span> appreciating
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            <span style={{ color: '#ef4444', fontWeight: 700 }}>{depreciating}</span> depreciating
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            {/* Vehicles with neither an invested nor a current figure to compare — not
                "everyone else", which would also sweep in a rare flat (0% gain) vehicle. */}
            <span style={{ color: 'var(--text3)', fontWeight: 700 }}>{unvaluedCount}</span> unvalued
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Invested</th>
                <th>Current Value</th>
                <th>Gain / Loss</th>
                <th>Running Costs</th>
                <th>Net Return</th>
                <th>Return %</th>
              </tr>
            </thead>
            <tbody>
              {vehicleData.map(v => (
                <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/vehicles/${v.id}`)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden', background: 'var(--surface2)', flexShrink: 0 }}>
                        {v.photo ? <img src={v.photo} alt="" style={{ width: '100%', height: '100%', ...cropStyle(v.photoRow) }} /> : <Car size={16} style={{ margin: '10px auto', display: 'block', color: 'var(--text3)' }} />}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{v.brand} {v.model}</div>
                        <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'DM Mono, monospace' }}>{v.registration_number} · {v.year}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {v.investedCapital ? (
                      <>
                        {formatCurrency(v.investedCapital)}
                        {v.restorationSpend > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>incl. {formatCurrency(v.restorationSpend)} restoration</div>
                        )}
                      </>
                    ) : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                  <td>
                    {v.currentValue ? (
                      <>
                        {formatCurrency(v.currentValue)}
                        {v.val?.valuation_comparables === 0 && (
                          <div style={{ fontSize: 10, fontWeight: 600, color: '#f97316', marginTop: 2 }}
                               title="No comparable sales matched this vehicle — this value isn't backed by market data">
                            ⚠ No market data
                          </div>
                        )}
                      </>
                    ) : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                  <td>
                    {v.gainLoss !== null ? (
                      <span style={{ color: v.gainLoss >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                        {v.gainLoss >= 0 ? '+' : ''}{formatCurrency(v.gainLoss)}
                      </span>
                    ) : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                  <td style={{ color: '#ef4444' }}>{v.runningCosts > 0 ? formatCurrency(v.runningCosts) : '—'}</td>
                  <td>
                    {v.netReturn !== null ? (
                      <span style={{ color: v.netReturn >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                        {v.netReturn >= 0 ? '+' : ''}{formatCurrency(v.netReturn)}
                      </span>
                    ) : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                  <td>
                    {v.returnPct !== null ? (
                      <span style={{ color: v.returnPct >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                        {v.returnPct >= 0 ? '+' : ''}{v.returnPct.toFixed(1)}%
                      </span>
                    ) : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface2)' }}>
                <td style={{ fontWeight: 700 }}>TOTAL ({vehicles.length} vehicles)</td>
                <td style={{ fontWeight: 700 }}>
                  {formatCurrency(totalInvestedAll)}
                  {totalRestorationAll > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>incl. {formatCurrency(totalRestorationAll)} restoration</div>
                  )}
                </td>
                <td style={{ fontWeight: 700 }}>{formatCurrency(totalCurrentValue)}</td>
                <td style={{ fontWeight: 700, color: totalGainLoss >= 0 ? '#22c55e' : '#ef4444' }}>
                  {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss)}
                  <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>{comparable.length} valued of {vehicles.length}</div>
                </td>
                <td style={{ fontWeight: 700, color: '#ef4444' }}>
                  {formatCurrency(comparableRunningCosts)}
                  {otherRunningCosts > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>+{formatCurrency(otherRunningCosts)} unvalued</div>
                  )}
                </td>
                <td style={{ fontWeight: 700, color: totalNetReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                  {totalNetReturn >= 0 ? '+' : ''}{formatCurrency(totalNetReturn)}
                </td>
                <td style={{ fontWeight: 700, color: totalReturnPct >= 0 ? '#22c55e' : '#ef4444' }}>
                  {totalReturnPct >= 0 ? '+' : ''}{totalReturnPct.toFixed(1)}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};